import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Avatar, Checkbox } from 'antd'
import {
    CommentOutlined,
    SoundOutlined,
    BulbOutlined,
    MessageOutlined,
} from '@ant-design/icons'
import { JEPNotificationType } from 'common/src/api/websiteFrontendVsWebsiteBackend/notifications/types'
import { ContentCreatorNotification } from 'common/src/api/contentFrontendVsContentBackend/types'
import { displayStringForCommentPostTime } from '@/utils/dateTime'
import {
    buildHrefForCommentContentTypeAndId,
    buildHrefForMnemonicSuggestion,
    invalidateContentCommentThread,
    prettifyContentType,
} from '@/components/NotificationModal/NotificationListItem/NotificationListItem.types'
import Link from 'next/link'
import styles from './NotificationQueueItem.module.less'
import { UrlObject } from 'url'
import { UserType } from 'magic-text-box-lexical/src/context/AnnotatedContentContext/AnnotatedContent.types'
import { useSetRecoilState } from 'recoil'
import {
    selectedFeedbackNotificationAtom,
    pendingMnemonicSuggestionAtom,
} from '@/atoms/notifications'

interface NotificationQueueItemProps {
    notification: ContentCreatorNotification & { id: string }
    onToggleComplete: (notificationId: string, isCompleted: boolean) => void
    onSaveNotes: (notificationId: string, notes: string) => void
}

export const NotificationQueueItem: React.FC<NotificationQueueItemProps> = (
    props
): ReactElement => {
    const { notification, onToggleComplete, onSaveNotes } = props
    const [notesValue, setNotesValue] = useState(notification.notes || '')
    const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | ''>('')
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
    const [isNotesExpanded, setIsNotesExpanded] = useState(!!notification.notes)
    const setSelectedFeedback = useSetRecoilState(
        selectedFeedbackNotificationAtom
    )
    const setPendingMnemonicSuggestion = useSetRecoilState(
        pendingMnemonicSuggestionAtom
    )

    useEffect(() => {
        setNotesValue(notification.notes || '')
    }, [notification.notes])

    const handleNotesChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newNotes = e.target.value
            setNotesValue(newNotes)
            setSaveStatus('saving')
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
            debounceTimerRef.current = setTimeout(() => {
                onSaveNotes(notification.id, newNotes)
                setSaveStatus('saved')
                setTimeout(() => setSaveStatus(''), 2000)
            }, 1500)
        },
        [notification.id, onSaveNotes]
    )

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    const { avatar, href, textToShow } = useMemo((): {
        avatar: ReactElement
        href: string | UrlObject | null
        textToShow: ReactElement
    } => {
        switch (notification.type) {
            case JEPNotificationType.COMMENT:
                return {
                    avatar: (
                        <Avatar
                            className={`${styles.avatar} ${
                                notification.metadata.commentUserType ===
                                UserType.instructor
                                    ? styles.avatarComment
                                    : styles.avatarComment
                            }`}
                            size="default"
                            icon={<CommentOutlined />}
                        />
                    ),
                    href: buildHrefForCommentContentTypeAndId(
                        notification.metadata.contentType,
                        notification.metadata.contentPath,
                        notification.metadata.contentId
                    ),
                    textToShow: (
                        <div>
                            <b>
                                {notification.metadata.commentUserDisplayName}
                            </b>{' '}
                            commented on{' '}
                            {prettifyContentType(
                                notification.metadata.contentType
                            )}
                            .
                        </div>
                    ),
                }
            case JEPNotificationType.ANNOUNCEMENT:
                return {
                    avatar: (
                        <Avatar
                            className={`${styles.avatar} ${styles.avatarAnnouncement}`}
                            size="default"
                            icon={<SoundOutlined />}
                        />
                    ),
                    href: notification.metadata.externalHref,
                    textToShow: (
                        <div>
                            <b>Announcement:</b> {notification.metadata.text}
                        </div>
                    ),
                }
            case JEPNotificationType.MNEMONIC_SUGGESTION:
                return {
                    avatar: (
                        <Avatar
                            className={`${styles.avatar} ${styles.avatarMnemonic}`}
                            size="default"
                            icon={<BulbOutlined />}
                        />
                    ),
                    href: buildHrefForMnemonicSuggestion(notification.metadata),
                    textToShow: (
                        <div>
                            <b>Mnemonic suggestion</b> from{' '}
                            {notification.metadata.userEmail}: &ldquo;
                            {notification.metadata.proposedMnemonic}&rdquo;
                            {notification.metadata.currentMnemonic && (
                                <span>
                                    {' '}
                                    (current: &ldquo;
                                    {notification.metadata.currentMnemonic}
                                    &rdquo;)
                                </span>
                            )}
                        </div>
                    ),
                }
            case JEPNotificationType.FEEDBACK:
                return {
                    avatar: (
                        <Avatar
                            className={`${styles.avatar} ${styles.avatarFeedback}`}
                            size="default"
                            icon={<MessageOutlined />}
                        />
                    ),
                    href: null,
                    textToShow: (
                        <div>
                            <b>Feedback</b> from{' '}
                            {notification.metadata.userEmail}: &ldquo;
                            {notification.metadata.feedback}&rdquo;
                        </div>
                    ),
                }
        }
    }, [notification])

    return (
        <div className={styles.queueItemContainer}>
            <div className={styles.queueItemRow}>
                <Checkbox
                    className={styles.queueItemCheckbox}
                    checked={notification.isCompleted}
                    onChange={(e) =>
                        onToggleComplete(notification.id, e.target.checked)
                    }
                />
                <div className={styles.queueItemAvatar}>{avatar}</div>
                {href === null ? (
                    <div
                        className={styles.queueItemContent}
                        onClick={() => setSelectedFeedback(notification)}
                    >
                        <div className={styles.queueItemText}>{textToShow}</div>
                        <div className={styles.queueItemTime}>
                            {displayStringForCommentPostTime(
                                notification.createdAt
                            )}
                        </div>
                    </div>
                ) : (
                    <Link
                        href={href}
                        className={styles.queueItemContent}
                        onClick={() => {
                            if (
                                notification.type ===
                                JEPNotificationType.COMMENT
                            ) {
                                invalidateContentCommentThread(
                                    notification.metadata
                                )
                            }
                            // The href lands on the notecard; hand the
                            // suggestion to the review panel in its editor.
                            if (
                                notification.type ===
                                JEPNotificationType.MNEMONIC_SUGGESTION
                            ) {
                                setPendingMnemonicSuggestion(notification)
                            }
                            // Don't call onCloseModal() here - the Link navigation
                            // changes the URL, which triggers useModalUrlSync to
                            // close the modal via the URL→atoms effect.
                        }}
                    >
                        <div className={styles.queueItemText}>{textToShow}</div>
                        <div className={styles.queueItemTime}>
                            {displayStringForCommentPostTime(
                                notification.createdAt
                            )}
                        </div>
                    </Link>
                )}
                <div
                    className={`${styles.queueItemDot} ${
                        notification.hasBeenSeen ? styles.hidden : ''
                    }`}
                >
                    &#9679;
                </div>
            </div>
            <div className={styles.queueItemNotes}>
                {isNotesExpanded ? (
                    <>
                        <textarea
                            className={styles.notesTextarea}
                            value={notesValue}
                            onChange={handleNotesChange}
                            placeholder="Add notes..."
                            onClick={(e) => e.stopPropagation()}
                        />
                        <div className={styles.notesSaveStatus}>
                            {saveStatus === 'saving'
                                ? 'Saving...'
                                : saveStatus === 'saved'
                                  ? 'Saved'
                                  : ''}
                        </div>
                    </>
                ) : (
                    <a
                        onClick={(e) => {
                            e.preventDefault()
                            setIsNotesExpanded(true)
                        }}
                        style={{ fontSize: 12, cursor: 'pointer' }}
                    >
                        Add notes
                    </a>
                )}
            </div>
        </div>
    )
}
